<template>
  <div class="container-nzidoo">
    <div class="chat-container">
      <!-- Contacts List -->
      <ContactsSidebar :contacts="filteredContacts" :selected-contact="selectedContact" :show-contacts="showContacts"
        :is-mobile="isMobile" @select-contact="selectContact" @filter-change="handleFilterChange" />

      <!-- Conversation Area -->
      <ConversationArea :selected-contact="selectedContact" :messages="filteredMessages" :message-input="messageInput"
        :is-mobile="isMobile" :show-contacts="showContacts" :user="user" @back="handleBack" @send-message="sendMessage"
        @update-message-input="messageInput = $event" @respond-offer="respondOffer" @move-to-product="moveToProductPage"
        @contact-product-loaded="handleContactProductLoaded" @show-offer-modal="handleShowOfferModal" />
    </div>

    <MakeAnOfferModal v-if="product && product.id" :products="selectedContact.offer.offer_products"
      :conversation-id="selectedContact.id" @offer-sent="reloadSelectedContactMessages" />

    <LoginModal v-if="$route" :route="$route.name" />
    <v-dialog />

    <notifications class="custom-notifications mt-2" group="success" position="top center" />
    <notifications class="custom-notifications mt-2" group="error" position="top center" />
  </div>
</template>

<script>
// import config from "@/config";
import ContactsSidebar from "@/components/messenger/ContactsSidebar.vue";
import ConversationArea from "@/components/messenger/ConversationArea.vue";
import MakeAnOfferModal from "@/components/modals/MakeAnOfferModal.vue";
import LoginModal from '@/components/modals/LoginModal.vue';

export default {
  name: "Messenger",
  components: {
    ContactsSidebar,
    ConversationArea,
    MakeAnOfferModal,
    LoginModal,
  },
  data() {
    return {
      selectedContact: null,
      messages: [],
      contacts: [],
      allContacts: [], // Store all contacts before filtering
      filterMode: 'active', // 'active' or 'archived'
      messageInput: '',
      isMobile: false,
      showContacts: true,
      product: null,
    };
  },
  computed: {
    user() {
      return this.$store.state.user;
    },
    filteredMessages() {
      return this.messages.filter(message => message.type!=='ABOUT');
    },
    filteredContacts() {
      if (this.filterMode==='active') {
        // Active ONLY if ALL products are neither deleted nor sold
        return this.allContacts.filter(contact => {
          const products=this.getContactProducts(contact);

          return products.every(product =>
            !product.deleted_at&&product.has_been_sold!==1
          );
        });

      } else {
        // Archived if ANY product is deleted OR sold
        return this.allContacts.filter(contact => {
          const products=this.getContactProducts(contact);

          return products.some(product =>
            product.deleted_at||product.has_been_sold===1
          );
        });
      }
    }

  },
  mounted() {
    this.checkMobile();
    window.addEventListener('resize', this.checkMobile);
    this.loadContacts();
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.checkMobile);
  },
  methods: {
    checkMobile() {
      this.isMobile=window.innerWidth<768;
      if (!this.isMobile) {
        this.showContacts=true;
      }
    },
    // Updated loadContacts method to handle new API response structure
    loadContacts() {
      this.$axios({
        url: "/conversations",
        method: "GET",
      }).then((response) => {
        const currentUserId=this.user?.id;

        this.allContacts=response.data.map(conversation => {

          const isCurrentUserBuyer=currentUserId===conversation.buyer_id;
          const isCurrentUserSeller=currentUserId===conversation.seller_id;

          let otherUser=null;
          if (isCurrentUserBuyer) {
            otherUser=conversation.seller;
          } else if (isCurrentUserSeller) {
            otherUser=conversation.buyer;
          }

          // ✅ LAST MESSAGE (already sorted by backend)

          const product=conversation.offer?.offer_products?.[0]?.product||null;

          return {
            id: conversation.id,
            offer_id: conversation.offer_id,

            ...(otherUser? {
              firstname: otherUser.firstname||'',
              username: otherUser.username||otherUser.firstname||'',
              picture: otherUser.picture||null,
              verified: otherUser.verified||false,
              is_online: otherUser.is_online||false,
            }:{}),

            offer: conversation.offer,
            product,

            // ✅ Correct last message info
            last_message: conversation.last_message_text||'',
            last_message_time: conversation.last_message_time||null,

            unread: conversation.unread,
            showOffer: conversation.showOffer,
          };
        });

        // ✅ SAFETY SORT (optional, backend already sorted)
        this.allContacts.sort((a, b) => {
          return new Date(b.last_message_time||0)-new Date(a.last_message_time||0);
        });

      });
    },
    selectContact(contact) {
      this.updateUnreadCount(contact, true);

      // Check if we're already viewing this conversation
      const routeId=parseInt(this.$route.params.id||0);
      const routeProduct=parseInt(this.$route.params.productId||this.$route.params.product_id||0);
      // Resolve a concrete product for this contact. Some contacts use
      // `contact.product` (single product) while others represent an offer
      // and expose multiple products under `contact.offer.offer_products`.
      // Ensure we have `contact.product` set to a product object so the
      // rest of the code (routing/api) can rely on it.
      let contactProduct=null;
      if (contact.product&&contact.product.id) {
        contactProduct=contact.product;
      } else if (contact.offer&&Array.isArray(contact.offer.offer_products)&&contact.offer.offer_products.length) {
        // offer_products items might be shaped as { product: { ... } }
        const first=contact.offer.offer_products[0];
        contactProduct=first&&(first.product||first);
      }

      // attach resolved product back to contact for consistency
      if (contactProduct) {
        // ensure product property exists and has id
        this.$set(contact, 'product', contactProduct);
      }

      const alreadySelected=this.selectedContact?.id===contact.id&&
        this.selectedContact?.product?.id===contact.product?.id;

      // Only fetch if we're switching to a different conversation
      if (alreadySelected&&routeId===contact.id&&routeProduct===contact.product.id) {
        if (this.isMobile) {
          this.showContacts=false;
        }
        return;
      }

      // If we don't have a product id (shouldn't happen), bail out early.
      if (!contact.offer||!contact.offer.id) return;
      this.$axios({
        url: "/conversations/"+contact.id,
        method: "GET",
      }).then((response) => {
        // Backend returns conversation object directly, not wrapped in data.messages
        const conversation=response.data;
        this.messages=conversation.messages||[];
        this.selectedContact=contact;
        contact.lastAboutMessage=this.extractLastAboutMessage(conversation.messages||[]);

        if (this.isMobile) {
          this.showContacts=false;
        }
      });
    },
    extractLastAboutMessage(messages) {
      if (!Array.isArray(messages)) return null;
      const aboutMessages=messages.filter(msg => msg.type==='ABOUT'||msg.type==='REQUEST_OFFER');
      if (aboutMessages.length===0) return null;
      return aboutMessages.reduce((latest, msg) =>
        new Date(msg.created_at)>new Date(latest.created_at)? msg:latest
      );
    },


    // Updated loadConversationById method
    loadConversationById(contactId, offerId) {
      // If offerId is provided, match both contact id and offer id. Otherwise match only contact id.
      const contact=this.allContacts.find(c => {
        if (!c) return false;
        if (c.id!==contactId) return false;
        if (offerId) {
          return c.offer&&c.offer.id===offerId;
        }
        return true;
      });
      if (!contact) return;
      // this.selectContact(contact);
    },
    sendMessage() {
      if (!this.messageInput.trim()||!this.selectedContact) return;

      const tempId='temp-'+Date.now();
      const tempMessage={
        id: tempId,
        from: this.user.id,
        product_id: this.selectedContact.product?.id,
        to: this.selectedContact.id,
        text: this.messageInput,
        pictures: [],
        created_at: new Date().toISOString(),
        sending: true,
        error: false,
      };

      this.messages.push(tempMessage);

      // Backend expects: { text: string, pictures: array }
      const requestData={
        text: this.messageInput,
        pictures: [] // Can be extended later for file uploads
      };

      this.messageInput='';

      this.$axios({
        url: "conversations/"+this.selectedContact.id+"/messages",
        method: "POST",
        data: requestData,
      })
        .then((response) => {
          const index=this.messages.findIndex(m => m.id===tempId);
          if (index!==-1) this.$set(this.messages, index, response.data);

          this.loadContacts();
          // Update store with messages
        })
        .catch((err) => {
          console.error(err);
          const index=this.messages.findIndex(m => m.id===tempId);
          if (index!==-1) this.$set(this.messages, index, {
            ...this.messages[index],
            sending: false,
            error: true,
          });

        });
    },
    updateUnreadCount(contact, reset) {
      this.allContacts=this.allContacts.map(single => {
        const isSameConversation=single.id===contact.id&&
          single.product&&contact.product&&
          single.product.id===contact.product.id;

        if (!isSameConversation) return single;

        if (reset) {
          single.unread=0;
        } else {
          single.unread=(single.unread||0)+1;
        }
        return single;
      });
      // Keep the Vuex store in sync with unread messages by refreshing messages
      // This ensures any global unread counters are updated as well.
      try {
        this.$store.dispatch('updateMessages');
      } catch (e) {
        // swallow - non-fatal if store isn't available in some contexts
        console.error('Failed to dispatch updateMessages:', e);
      }
    },
    handleBack() {
      this.showContacts=true;
      this.$router.push({ name: 'messenger' });
    },
    moveToProductPage(selectedproduct) {
      this.$router.push({ name: "productDetails", params: { id: selectedproduct.id } });
    },
    respondOffer(offerId, response) {
      this.$axios({
        url: `/offers/respond/${offerId}`,
        method: 'POST',
        data: { response }
      }).then(() => {
        // after responding to an offer, reload the current conversation messages
        if (this.selectedContact) {
          this.reloadSelectedContactMessages();

        }
        this.loadContacts();
      }).catch((err) => console.log(err));
    },
    handleContactProductLoaded(contact, product) {
      this.$set(contact, 'productData', {
        ...product,
        user_id: product.user_id||product.user?.id
      });
    },
    handleShowOfferModal(product) {
      this.product={
        ...product,
        user_id: product.user_id||product.user?.id
      };
    },
    reloadSelectedContactMessages() {
      if (!this.selectedContact) return;
      this.$axios({
        url: `/conversations/${this.selectedContact.id}`,
        method: "GET",
      }).then((response) => {
        // Backend returns conversation object directly
        const conversation=response.data;
        this.messages=conversation.messages||[];
        this.selectedContact.lastAboutMessage=this.extractLastAboutMessage(conversation.messages||[]);
        this.loadContacts();
      }).catch((err) => {
        console.error("Failed to reload messages:", err);
      });
    },
    handleFilterChange(mode) {
      this.filterMode=mode;
    },
    getContactProducts(contact) {
      // Extract products from contact, handling both single product and offer_products array
      if (contact.offer&&Array.isArray(contact.offer.offer_products)&&contact.offer.offer_products.length) {
        return contact.offer.offer_products
          .map(op => op&&op.product)
          .filter(p => p);
      }
      if (contact.product) {
        return [contact.product];
      }
      return [];
    },
  }
};
</script>

<style scoped>
.chat-container {
  display: flex;
  height: calc(100vh - 130px);
  background: white;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.custom-notifications {
  width: 95%;
}

@media (max-width: 767px) {
  .custom-notifications {
    width: 35%;
  }
}
</style>